import {
  Box, FormGroup, FormControlLabel, Switch, Typography, Divider, Paper, Chip, Stack,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import WarningIcon from '@mui/icons-material/Warning';
import DevicesIcon from '@mui/icons-material/Devices';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Protocolos / kinds de edge no mapa.
 */
const PROTOCOLS = [
  { key: 'bgp',          label: 'BGP',             color: '#e67e22', kinds: ['bgp'] },
  { key: 'ospf',         label: 'OSPF',            color: '#9b59b6', kinds: ['ospf_adj'] },
  { key: 'p2p',          label: 'p2p /30',         color: '#3498db', kinds: ['p2p'] },
  { key: 'trunk',        label: 'Trunk',           color: '#14b8a6', kinds: ['trunk'] },
  { key: 'vlan_lan',     label: 'VLAN / LAN',      color: '#16a085', kinds: ['shared_lan'] },
  { key: 'l2',           label: 'L2 inferido',     color: '#95a5a6', kinds: ['l2_desc'] },
  { key: 'static',       label: 'Rotas estáticas', color: '#7f8c8d', kinds: ['static_route'] },
];

/**
 * Conta quantas arestas existem por protocolo no grafo atual.
 *
 * Por que isso importa: os chips eram fixos no código, e o resultado foi um
 * chip de MPLS filtrando por um tipo de aresta que o backend nunca emite —
 * ligar MPLS não podia mostrar nada, por construção — enquanto `trunk`, que o
 * backend emite, não tinha chip nenhum. Um controle sem efeito ensina o
 * operador que a interface mente, e a partir daí ele desconfia dos filtros que
 * funcionam.
 *
 * Com a contagem em mãos: chip com 0 não é renderizado, e os demais mostram
 * quanto vão afetar antes do clique.
 */
export function countByProtocol(edges) {
  const porKind = new Map();
  for (const e of edges || []) {
    for (const k of e.kinds || []) porKind.set(k, (porKind.get(k) || 0) + 1);
  }
  const out = new Map();
  for (const p of PROTOCOLS) {
    let n = 0;
    for (const k of p.kinds) n += porKind.get(k) || 0;
    out.set(p.key, n);
  }
  return out;
}

/** Só os protocolos presentes no grafo, com a contagem junto. */
export function protocolsPresent(edges) {
  const contagem = countByProtocol(edges);
  return PROTOCOLS
    .map((p) => ({ ...p, count: contagem.get(p.key) || 0 }))
    .filter((p) => p.count > 0);
}

/**
 * Filtros de HOSTS por papel/categoria. Mapeia cada chip a uma função predicado
 * que recebe o node bruto e retorna boolean. Agrupados por categoria pro sidebar.
 */
const HOST_FILTER_GROUPS = [
  {
    title: 'BGP',
    color: '#e67e22',
    chips: [
      { key: 'bgp_edge', label: 'eBGP borda',   color: '#e67e22',
        test: (n) => n.bgp_role === 'ebgp_edge' },
      { key: 'bgp_ibgp', label: 'iBGP interno', color: '#d35400',
        test: (n) => n.bgp_role === 'ibgp_only' },
      { key: 'bgp_rr',   label: 'Route Reflector', color: '#c0392b',
        test: (n) => n.bgp_role === 'rr' },
      { key: 'bgp_ext',  label: 'AS externo',   color: '#f39c12',
        test: (n) => n.is_virtual === true },
    ],
  },
  {
    title: 'OSPF',
    color: '#9b59b6',
    chips: [
      { key: 'ospf_backbone',    label: 'Backbone',      color: '#9b59b6',
        test: (n) => n.ospf_role === 'backbone' },
      { key: 'ospf_nonbackbone', label: 'Non-backbone',  color: '#8e44ad',
        test: (n) => n.ospf_role === 'non_backbone' },
      { key: 'ospf_abr',         label: 'ABR (multi-área)', color: '#6c3483',
        test: (n) => n.ospf_role === 'abr' },
    ],
  },
  {
    title: 'Outros',
    color: '#16a085',
    chips: [
      { key: 'mpls',   label: 'MPLS habilitado', color: '#2ecc71',
        test: (n) => n.has_mpls === true },
      { key: 'vlans',  label: 'Tem VLANs',       color: '#16a085',
        test: (n) => n.has_vlans === true },
    ],
  },
];


// Mapa achatado: key → testFn (pra CyTopologyMap aplicar o filtro)
export const HOST_FILTER_TESTS = {};
for (const g of HOST_FILTER_GROUPS) {
  for (const c of g.chips) HOST_FILTER_TESTS[c.key] = c.test;
}


/**
 * LayerToggles (enxuto pós-refactor ViewMode):
 *   - Só mostra chips de filtro de host por papel (BGP/OSPF/MPLS/VLAN)
 *   - Toggle de "Issues overlay"
 *
 * A seção antiga "Links (protocolo)" com switches BGP/OSPF/MPLS/p2p foi removida
 * porque ViewMode (L1/L2/L3 sub-picker/Serviços) já decide quais kinds de edge
 * aparecem. Manter os 2 mecanismos lado-a-lado confundia o operador.
 */
export default function LayerToggles({
  showIssues,
  onToggleIssues,
  activeHostFilters,
  onToggleHostFilter,
}) {
  return (
    <Paper sx={{ p: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DevicesIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={600}>
          Filtrar hosts por papel
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />

      {HOST_FILTER_GROUPS.map((group) => (
        <Accordion
          key={group.title}
          disableGutters
          defaultExpanded={group.title === 'BGP'}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ color: group.color }}>
              {group.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {group.chips.map((c) => {
                const active = activeHostFilters && activeHostFilters.has(c.key);
                return (
                  <Chip
                    key={c.key}
                    label={c.label}
                    size="small"
                    onClick={() => onToggleHostFilter && onToggleHostFilter(c.key)}
                    sx={{
                      backgroundColor: active ? c.color : 'transparent',
                      color: active ? '#fff' : 'text.primary',
                      border: `1.5px solid ${c.color}`,
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: active ? c.color : `${c.color}22` },
                    }}
                  />
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Sem filtro = mostra todos. OR entre chips ativos.
      </Typography>

      <Divider sx={{ my: 1 }} />

      <FormControlLabel
        sx={{ mb: 0 }}
        control={
          <Switch
            size="small"
            checked={showIssues}
            onChange={onToggleIssues}
            color="error"
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningIcon fontSize="small" color="error" />
            <Typography variant="body2">Issues (overlay)</Typography>
          </Box>
        }
      />
    </Paper>
  );
}


// Helper pra converter set de protocolos ativos em set de kinds pra filtrar edges
export function protocolsToKinds(activeProtocols) {
  const kinds = new Set();
  for (const p of PROTOCOLS) {
    if (activeProtocols.has(p.key)) {
      for (const k of p.kinds) kinds.add(k);
    }
  }
  return kinds;
}
